import { Id, LaravelResponse } from "@dgs/core";
import { AxiosResponse } from "axios";
import { useCallback, useState } from "react";
import { TenantIndexResource } from "../../../../shared/types/tenant";
import { ArchiveByTenantRequest } from "../types/archive";
import { TenantArchiveFormState } from "./TenantArchiveDrawer";
import { ITenantArchiveService, useArchiveAction, useRestoreAction } from "./useArchiveRestoreByTenantAction";

type IRestoreService = ITenantArchiveService & {
	getArchivedTenants: (id: Id) => Promise<AxiosResponse<LaravelResponse<TenantIndexResource[]>>>;
};
type IArchiveService = ITenantArchiveService & {
	getTenants: (id: Id) => Promise<AxiosResponse<LaravelResponse<TenantIndexResource[]>>>;
};

export const useRestoreByTenantHandler = (service: IRestoreService, onActionSuccess?: () => void) => {
	const actionHandler = useRestoreAction(service);

	return useBaseHandler(actionHandler, service.getArchivedTenants, onActionSuccess);
};

export const useArchiveByTenantHandler = (service: IArchiveService, onActionSuccess?: () => void) => {
	const actionHandler = useArchiveAction(service);

	return useBaseHandler(actionHandler, service.getTenants, onActionSuccess);
};

const useBaseHandler = (
	action: (id: Id, request: ArchiveByTenantRequest) => Promise<boolean>,
	fetch: (id: Id) => Promise<AxiosResponse<LaravelResponse<TenantIndexResource[]>>>,
	onActionSuccess?: () => void,
) => {
	const [entityId, setEntityId] = useState<Id | null>(null);
	const [confirmationEntity, setConfirmationEntity] = useState<{ id: Id; tenantId: Id } | null>(null);
	const clickHandler = useCallback(
		async (id: Id) => {
			const tenants = await fetch(id).then((r) => r.data.data);

			if (tenants.length === 1) {
				setConfirmationEntity({
					id,
					tenantId: tenants[0].id,
				});
			} else {
				setEntityId(id);
			}
		},
		[fetch],
	);
	const confirmationAction = async () => {
		if (confirmationEntity) {
			onActionSuccess && onActionSuccess();
			await action(confirmationEntity.id, { tenantIds: [confirmationEntity.tenantId] });
			setEntityId(null);
			setConfirmationEntity(null);
		}
	};
	const onSubmit = async (values: TenantArchiveFormState) => {
		onActionSuccess && onActionSuccess();
		const res = await action(entityId as Id, { tenantIds: values.tenants.map((tenant) => tenant.id) });
		if (res) {
			setEntityId(null);
			setConfirmationEntity(null);
		}
		return res;
	};

	const drawerProps = {
		onClose: () => {
			setEntityId(null);
			setConfirmationEntity(null);
		},
		entityId,
		onSubmit,
	};
	const confirmationProps = {
		open: confirmationEntity !== null,
		onClose: () => setConfirmationEntity(null),
		action: confirmationAction,
	};

	return {
		clickHandler,
		drawerProps,
		confirmationProps,
	};
};
